/**
 * The customer-facing chat contract (app.wundo.ai chat drawer).
 *
 * Everything the browser may see is enumerated here. The backend serializer
 * builds these objects field by field from the message row — it never spreads
 * one — so internal machinery (tool arguments and results, reasoning, trace
 * ids, meta, skill/language/delivery bookkeeping) cannot reach a customer by
 * accident. A step Wundo takes reaches the browser only as an `activity`
 * event, and later as that same step kept with the reply: plain words written
 * for the user, never the tool or its data.
 */

/**
 * Longest text a single chat message may carry. The same ceiling as a meeting's
 * private notes, so a pasted verslag or transcript fits in one turn.
 */
export const MAX_CHAT_MESSAGE_LENGTH = 50_000;

/** Plain assistant or user text. */
export interface ChatTextBody {
  type: 'text';
  text: string;
}

/** Text that carries a single link, e.g. a sign-in link for an integration. */
export interface ChatLinkBody {
  type: 'link';
  text: string;
  link_text: string;
  url: string;
}

export interface ChatCardSection {
  label: string;
  content: string;
}

export interface ChatCardButton {
  id: string;
  title: string;
  /** When set, the button opens this URL instead of sending its title. */
  url?: string;
}

/** A rich message: header, body, labelled sections and buttons. */
export interface ChatCardBody {
  type: 'card';
  header?: string;
  body?: string;
  footer?: string;
  sections?: ChatCardSection[];
  buttons?: ChatCardButton[];
}

export interface ChatTemplateOption {
  id: string;
  title: string;
  description?: string;
}

/** A question with preset answers. Picking one sends its title. */
export interface ChatTemplateBody {
  type: 'template';
  text: string;
  options: ChatTemplateOption[];
  allow_multiple: boolean;
}

/** How a shared file is shown: an image as a picture, anything else as a chip. */
export type ChatAttachmentKind = 'image' | 'file';

/**
 * One file the user shared. `url` is an address on the API, not a storage key
 * — where the bytes actually live never leaves the backend — and it answers
 * only to the person whose conversation the file is in.
 */
export interface ChatAttachmentFile {
  kind: ChatAttachmentKind;
  name: string;
  mime_type: string;
  size: number;
  url: string;
}

/** Files the user shared, and whatever they wrote alongside them. */
export interface ChatAttachmentBody {
  type: 'attachment';
  text?: string;
  files: ChatAttachmentFile[];
}

export type ChatMessageBody =
  | ChatTextBody
  | ChatLinkBody
  | ChatCardBody
  | ChatTemplateBody
  | ChatAttachmentBody;

/**
 * A file that has been uploaded but not yet sent. It becomes part of a message
 * by being named in the send; until then it is only stored, and an upload
 * nobody sends is simply never spoken of again.
 */
export interface ChatAttachmentUpload {
  id: string;
  kind: ChatAttachmentKind;
  name: string;
  mime_type: string;
  size: number;
}

/**
 * What the user had open while they wrote.
 *
 * Travels with one message rather than with the conversation: the drawer stays
 * where it is while the user walks around the app, so the page they were on is
 * a fact about the sentence they just typed and about nothing else. A message
 * sent from somewhere else carries no anchor, which is how leaving a page is
 * said.
 */
export interface ChatMeetingAnchor {
  type: 'meeting';
  id: string;
}

/**
 * The practice page, which carries no id: there is one of it, and what stands
 * on it is decided by who is reading rather than by which row was opened.
 */
export interface ChatPracticeAnchor {
  type: 'practice';
}

/** One journey, as its manager follows it on the journey page. */
export interface ChatJourneyAnchor {
  type: 'journey';
  id: string;
}

/**
 * One colleague, as the user sees them on their colleague page. Named by the
 * colleague's id, which stands for the whole person: every address that turned
 * out to be them.
 */
export interface ChatPersonAnchor {
  type: 'person';
  id: string;
}

export type ChatAnchor =
  | ChatMeetingAnchor
  | ChatPracticeAnchor
  | ChatJourneyAnchor
  | ChatPersonAnchor;

export type ChatMessageAuthor = 'user' | 'wundo';

export interface ChatMessage {
  /**
   * Identifies a message within its conversation — a key to render by, and the
   * way to tell an arriving message from one already shown. A reply is pushed
   * the moment it is written and stored just after, so a live message and the
   * same message read back later do not carry the same value: load history
   * once and append what arrives after, rather than merging the two.
   */
  id: string;
  conversation_id: string;
  author: ChatMessageAuthor;
  body: ChatMessageBody;
  created_at: string;
  /**
   * Wundo's own line about attachments it could not use. Not the turn's
   * reply: a turn still being written goes on after it.
   */
  notice?: true;
  /**
   * The steps Wundo took before this reply began, as they ended — the same
   * rows the drawer showed while the turn ran, so a conversation read back
   * shows them too. Only on Wundo's replies, and only when there were any.
   */
  activity?: ChatActivityStepTaken[];
}

export interface ChatConversation {
  id: string;
  /** Null while the conversation has no messages yet. */
  last_message_at: string | null;
}

/**
 * What the drawer opens into: the running sitting, or nothing when the last
 * one has gone quiet and the next thing said starts a fresh one. Earlier
 * conversations are not named here — they are found through the history.
 */
export interface ChatStateResponse {
  conversation: ChatConversation | null;
}

/** One earlier conversation, as it appears in the history list. */
export interface ChatConversationSummary {
  id: string;
  last_message_at: string;
  message_count: number;
  /** The opening line, which is what tells one conversation from another. */
  preview: string;
  /** Whether this is the sitting currently running. */
  current: boolean;
}

export interface ChatSendResponse {
  conversation_id: string;
  message: ChatMessage;
}

/** A message arrived — the user's own (echoed) or Wundo's reply. */
export interface ChatMessageEvent {
  type: 'message';
  message: ChatMessage;
}

/** Wundo is working on a reply for this conversation. */
export interface ChatTypingEvent {
  type: 'typing';
  conversation_id: string;
}

/**
 * A piece of a reply that is still being written.
 *
 * `message_id` is the row the reply is being written into — it exists before
 * the first piece is sent, so every piece names the same message, and the
 * `message` event carrying that id is what says the reply is finished. `text`
 * is what to add to the end of what has arrived so far, never the whole reply,
 * so a piece that is missed cannot be told from one that is not.
 */
export interface ChatDeltaEvent {
  type: 'delta';
  conversation_id: string;
  message_id: string;
  text: string;
}

/** What kind of thing a step touches, shown as a small picture beside its text. */
export type ChatActivityIcon =
  | 'calendar'
  | 'search'
  | 'person'
  | 'memory'
  | 'mail'
  | 'reminder'
  | 'document'
  | 'template'
  | 'practice'
  | 'conversation'
  | 'journey'
  | 'page';

/** The name of something in a step's text that the user can open, and its page. */
export interface ChatActivityLink {
  /** Words that appear as-is in the step's `text`. */
  text: string;
  url: string;
}

/**
 * One thing a step touched, as a line under the unfolded step that opens to
 * its full text: a memory, a document, a section of a preparation. `body` is
 * plain text with line breaks; `meta` is a short note such as when it was saved.
 */
export interface ChatActivityItem {
  title: string;
  body?: string;
  meta?: string;
  href?: string;
}

/**
 * One step Wundo takes while working on a reply, told as it happens. `text` is
 * written for the user in their language — what it is doing at `start`, what
 * it did at `done` — and `step_id` ties the phases of one step together.
 * `failed` ends a step that did not work, with its `start` text.
 */
export interface ChatActivityEvent {
  type: 'activity';
  conversation_id: string;
  step_id: string;
  phase: 'start' | 'done' | 'failed';
  text: string;
  icon: ChatActivityIcon;
  /** What the step touched, listed when the user unfolds it, e.g. the memories it read. */
  detail?: ChatActivityItem[];
  link?: ChatActivityLink;
}

/** A step as it ended, kept with the reply it came before. */
export type ChatActivityStepTaken = Omit<
  ChatActivityEvent,
  'type' | 'conversation_id' | 'phase'
> & { phase: 'done' | 'failed' };

export type ChatEvent =
  | ChatMessageEvent
  | ChatTypingEvent
  | ChatDeltaEvent
  | ChatActivityEvent;

/** The websocket room carrying a user's own chat events. */
export function chatRoomName(userId: string): string {
  return `chat:userId:${userId}`;
}
